import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GridComponent, PdfExportProperties } from '@syncfusion/ej2-react-grids';
import { useGetScheduleExportQuery } from '../../../../../../services/EncounterService';
import type { ScheduleItemType } from '../../../../../../services/EncounterService/types/scheduleTypes';

export const useScheduleExportDialog = (onClose: () => void) => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get("company") || '';
  const center = searchParams.get("center") || '';
  const gridRef = useRef<GridComponent>(null);

  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Skip the query until user clicks export
  const [shouldFetch, setShouldFetch] = useState(false);

  const { data: scheduleData, isLoading, error } = useGetScheduleExportQuery(
    {
      company,
      center,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    },
    {
      skip: !shouldFetch || !startDate || !endDate,
    }
  );

  const canExport = startDate && endDate && company && center;

  // Transform data for the hidden grid
  const transformedData = scheduleData?.map(item => {
    const formatDateTime = (dateTimeStr: string): string => {
      if (!dateTimeStr) return '';
      
      try {
        let dateTime = new Date(dateTimeStr);
        
        if (isNaN(dateTime.getTime())) {
          const today = new Date().toISOString().split('T')[0];
          dateTime = new Date(`${today}T${dateTimeStr}`);
        }
        
        if (isNaN(dateTime.getTime())) {
          const baseDate = startDate || new Date().toISOString().split('T')[0];
          dateTime = new Date(`${baseDate}T${dateTimeStr}`);
        }
        
        if (isNaN(dateTime.getTime())) {
          return dateTimeStr;
        }
        
        return dateTime.toLocaleString('en-US', {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        console.warn('Date parsing error:', error, 'for value:', dateTimeStr);
        return dateTimeStr;
      }
    };
    
    return {
      ...item,
      patientName: `${item.patientLastName || ''}, ${item.patientFirstName || ''}`.trim(),
      startTime: formatDateTime(item.startTime),
      endTime: formatDateTime(item.endTime)
    };
  }) || [];

  const generateSyncfusionPDF = useCallback((data: ScheduleItemType[]) => {
    if (!gridRef.current) return;

    const pdfExportProperties: PdfExportProperties = {
      fileName: `Schedule_Export_${company}_${center}_${startDate}_to_${endDate}.pdf`,
      pageOrientation: 'Landscape',
      header: {
        fromTop: 0,
        height: 130,
        contents: [
          {
            type: 'Text',
            value: 'Schedule Export',
            position: { x: 0, y: 50 },
            style: { textBrushColor: '#000000', fontSize: 20 }
          },
          {
            type: 'Text',
            value: `Company: ${company} | Center: ${center}`,
            position: { x: 0, y: 75 },
            style: { textBrushColor: '#666666', fontSize: 12 }
          },
          {
            type: 'Text',
            value: `Date Range: ${startDate} to ${endDate}`,
            position: { x: 0, y: 95 },
            style: { textBrushColor: '#666666', fontSize: 12 }
          },
          {
            type: 'Text',
            value: `Generated: ${new Date().toLocaleString()}`,
            position: { x: 0, y: 115 },
            style: { textBrushColor: '#666666', fontSize: 10 }
          }
        ]
      }
    };

    gridRef.current.pdfExport(pdfExportProperties);
  }, [company, center, startDate, endDate]);

  // Generate PDF when data is loaded
  useEffect(() => {
    if (scheduleData && isExporting && !isLoading && gridRef.current) {
      generateSyncfusionPDF(scheduleData);
      setIsExporting(false);
      setShouldFetch(false);
      onClose();
    }
  }, [scheduleData, isExporting, isLoading, generateSyncfusionPDF, onClose]);

  const handleExport = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    setIsExporting(true);
    setShouldFetch(true);
  }, [startDate, endDate]);

  const handleClose = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setIsExporting(false);
    setShouldFetch(false);
    onClose();
  }, [onClose]);

  return {
    company,
    center,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    isLoading,
    isExporting,
    error,
    canExport,
    transformedData,
    gridRef,
    handleExport,
    handleClose,
  };
};
